import { queryClient } from '@/router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { MoveVerticalIcon, PlusCircleIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/button/button'
import { Checkbox } from '@/components/checkbox/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/popover'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { useBooleanState } from '@/lib/hooks/use-boolean-state'
import { updateTaskActionStatusMutation } from '@/lib/queries/business/v1/task/action/complete/mutations'
import { taskActionsQuery } from '@/lib/queries/business/v1/task/action/queries'
import { cn } from '@/lib/utils'
import { type ValueOf } from '@/lib/utils/type'

import { ACTION_STATUS } from './constants'
import { CreateTaskActionDialog } from './create-task-action-dialog'
import { DeleteTaskActionDialogTrigger } from './delete-task-action-dialog-trigger'

export interface CellContentActionListProps {
  taskId: number
}

export const CellContentActionList = ({ taskId }: CellContentActionListProps) => {
  const {
    booleanState: isOpenCreateTaskActionDialog,
    setToTrue: openCreateTaskActionDialog,
    setToFalse: closeCreateTaskActionDialog,
  } = useBooleanState(false)
  const {
    booleanState: isOpenActionListPopover,
    setToTrue: openActionListPopover,
    setToFalse: closeActionListPopover,
  } = useBooleanState(false)

  const { data: taskActionsData } = useQuery(taskActionsQuery(taskId))

  const { mutateAsync: updateTaskActionStatus, isPending: isUpdatingTaskActionStatus } =
    useMutation(updateTaskActionStatusMutation)

  const [actionList, setActionList] = useState(taskActionsData?.actionList ?? [])

  const status =
    !taskActionsData || taskActionsData?.completeActionCount === 0
      ? ACTION_STATUS.todo
      : taskActionsData?.completeActionCount < taskActionsData?.totalActionCount
        ? ACTION_STATUS.inProgress
        : ACTION_STATUS.done
  const checkedActionsCount = actionList.filter(({ isComplete }) => isComplete).length
  const realtimeStatus =
    checkedActionsCount === 0
      ? ACTION_STATUS.todo
      : checkedActionsCount < actionList.length
        ? ACTION_STATUS.inProgress
        : ACTION_STATUS.done

  const isLoadingSaveButton = isUpdatingTaskActionStatus
  const isDisabledCheckbox = isUpdatingTaskActionStatus

  const handleChangeCheckedAction = (actionId: number) => {
    setActionList((prev) => {
      const newActionList = prev.map((action) => {
        return action.actionId === actionId ? { ...action, isComplete: !action.isComplete } : action
      })
      return newActionList
    })
  }

  const handleChangeActionListPopoverOpen = (open: boolean) => {
    // NOTE: prevent the popover from being closed when the dialog opens
    if (!open && !isOpenCreateTaskActionDialog) {
      closeActionListPopover()
      return
    }
    openActionListPopover()
  }

  const handleClickCreateTaskButton = () => {
    openCreateTaskActionDialog()
  }

  const handleClickSaveButton = async () => {
    await updateTaskActionStatus(
      {
        taskId,
        data: actionList.map(({ actionId, isComplete }) => ({
          id: actionId,
          isComplete,
        })),
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: [API_ENDPOINTS.business.v1.task.search.index],
          })
          void queryClient.invalidateQueries({
            queryKey: [API_ENDPOINTS.business.v1.task.action.index(taskId)],
          })
        },
      }
    )
  }

  useEffect(() => {
    if (taskActionsData) {
      setActionList(taskActionsData.actionList)
    }
  }, [taskActionsData])

  return (
    <>
      <div>
        <Popover open={isOpenActionListPopover} onOpenChange={handleChangeActionListPopoverOpen}>
          <PopoverTrigger>
            <StatusBadgeTrigger status={status} />
          </PopoverTrigger>
          <PopoverContent align="start" className={cn('w-[180px]', 'p-0')}>
            <div className={cn('flex items-center', 'border-b border-border', 'px-3 py-2')}>
              <StatusBadge status={realtimeStatus} />
              <p className={cn('ml-2', 'text-base font-medium text-slate-900')}>
                ({checkedActionsCount}/{taskActionsData?.totalActionCount ?? '-'})
              </p>
            </div>
            <div className={cn('flex flex-col', 'border-b border-border', 'p-1')}>
              <div className={cn('flex flex-col gap-y-3', 'px-2 py-1.5')}>
                {actionList.map(({ actionId, title, isComplete, isDefaultAction }) => (
                  <div key={actionId} className={cn('flex items-center')}>
                    <label className={cn('flex items-center gap-x-2', 'cursor-pointer')}>
                      <Checkbox
                        variant="black"
                        checked={isComplete}
                        disabled={isDisabledCheckbox}
                        onCheckedChange={() => handleChangeCheckedAction(actionId)}
                      />
                      <span className={cn('text-sm font-normal text-foreground')}>{title}</span>
                    </label>
                    {!isDefaultAction && (
                      <DeleteTaskActionDialogTrigger taskId={taskId} actionId={actionId} actionName={title} />
                    )}
                  </div>
                ))}
              </div>

              <Button
                type="button"
                variant="ghost"
                className={cn('inline-flex justify-start gap-x-2', 'h-8', 'px-2 py-1.5')}
                onClick={handleClickCreateTaskButton}
              >
                <span>
                  <PlusCircleIcon size={16} />
                </span>
                <span className={cn('text-sm font-normal text-foreground')}>Create Other Task</span>
              </Button>
            </div>
            <div className={cn('flex justify-end', 'p-1')}>
              <Button
                type="button"
                variant="default"
                size="sm"
                loading={isLoadingSaveButton}
                onClick={handleClickSaveButton}
              >
                Save
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <CreateTaskActionDialog
        open={isOpenCreateTaskActionDialog}
        taskId={taskId}
        onClose={closeCreateTaskActionDialog}
      />
    </>
  )
}

const StatusBadgeTrigger = ({ status }: { status: ValueOf<typeof ACTION_STATUS> }) => {
  return (
    <div
      className={cn(
        'flex items-center justify-center gap-x-1',
        'text-xs font-medium',
        'px-2 py-1.5',
        'rounded-[5px] border',
        {
          'border-red-300 bg-red-100 text-red-900': status === ACTION_STATUS.todo,
          'border-yellow-300 bg-amber-100 text-yellow-900': status === ACTION_STATUS.inProgress,
          'border-green-300 bg-green-100 text-green-900': status === ACTION_STATUS.done,
        }
      )}
    >
      <MoveVerticalIcon size={16} />
      {status === ACTION_STATUS.todo && 'To do'}
      {status === ACTION_STATUS.inProgress && 'In Progress'}
      {status === ACTION_STATUS.done && 'Done'}
    </div>
  )
}

const StatusBadge = ({ status }: { status: ValueOf<typeof ACTION_STATUS> }) => {
  return (
    <div
      className={cn(
        'flex items-center justify-center',
        'w-fit',
        'px-2 py-1',
        'text-xs font-medium',
        'rounded-[5px] border',
        {
          'border-red-300 bg-red-100 text-red-900': status === ACTION_STATUS.todo,
          'border-yellow-300 bg-amber-100 text-yellow-900': status === ACTION_STATUS.inProgress,
          'border-green-300 bg-green-100 text-green-900': status === ACTION_STATUS.done,
        }
      )}
    >
      {status === ACTION_STATUS.todo && 'To do'}
      {status === ACTION_STATUS.inProgress && 'In Progress'}
      {status === ACTION_STATUS.done && 'Done'}
    </div>
  )
}
